import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Icon, Text } from "@zorni/components-lib";

import { useAdmin } from "../../hooks/useAdmin";
import { useOrganismeData } from "./hooks/useOrganismeData";
import { USER_ROLE, ORGANISME_STATUS } from "../../common/model/enum";
import Preloader from "../../../common/preLoader/PreLoader";
import AccountActivationRequired from "../../common/accountActivation/AccountActivationRequired";
import Timeline from "../../../common/timeline/Timeline";

import styles from "./Organisme.module.css";

const ADDRESS_ICON = "iconLocation";
const COUNTRY_ICON = "iconGlobe";
const STATUS_ICON = "iconCompliance";
const NOTE_ICON_PUBLIC = "iconInfo";
const NOTE_ICON_REVALIDATE = "iconTimer";

const formatAddressLines = (address) => {
  if (!address) return { line1: "", line2: "", line3: "" };
  const line1 = [address.number, address.route].filter(Boolean).join(" ");
  const line2 = [address.complement, address.stage].filter(Boolean).join(" · ");
  const line3 = [address.zipPostal, address.city].filter(Boolean).join(" ");
  return { line1, line2, line3 };
};

const Organisme = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { organization } = useAdmin();
  const { organisme, loading } = useOrganismeData(organization?.entityId);

  if (loading) {
    return (
      <div className={styles.centerState}>
        <Preloader />
      </div>
    );
  }

  const isValidated = organisme?.status === ORGANISME_STATUS.COMPLETED;
  const { line1, line2, line3 } = formatAddressLines(organisme?.address);
  const canEdit = organization?.role === USER_ROLE.PSA;

  // Le statut du dossier ne connaît aujourd'hui que 2 valeurs côté backend
  // (MISSING_INFORMATIONS / COMPLETED) : "Créé" reflète simplement l'existence
  // du dossier, "En validation" reste l'étape courante tant qu'il n'est pas
  // COMPLETED.
  const statusSteps = [
    { id: "created", status: "done", label: t("organism.status.timeline.created", "Créé") },
    {
      id: "in-review",
      status: isValidated ? "done" : "current",
      label: t("organism.status.timeline.inReview", "En validation"),
    },
    {
      id: "validated",
      status: isValidated ? "done" : "locked",
      label: t("organism.status.timeline.validated", "Validé"),
    },
  ];

  return (
    <div className={styles.root}>
      <div className={styles.pageHead}>
        <h1>{t("organism.pageTitle", "Mon organisme")}</h1>
        <p>
          {t(
            "organism.pageSubtitle",
            "Consultez les informations légales de votre organisme ou passez en mode édition pour les mettre à jour."
          )}
        </p>
      </div>

      {!organization ? (
        <AccountActivationRequired />
      ) : (
        <section className={styles.orgCard}>
          <div className={styles.orgHead}>
            <span className={styles.orgLogoRing}>
              {organisme?.picture ? (
                <img
                  className={styles.orgHeadLogo}
                  src={organisme.picture}
                  alt={organisme?.name || t("organism.label")}
                />
              ) : (
                <span className={styles.orgHeadLogoFallback}>
                  {(organisme?.name || "?").charAt(0).toUpperCase()}
                </span>
              )}
            </span>
            <div className={styles.orgIdText}>
              <strong>{organisme?.name || "—"}</strong>
            </div>
          </div>

          <div className={styles.orgBody}>
            <div className={styles.kycGrid}>
              <div className={styles.panel}>
                <p className={styles.panelTitle}>
                  <span className={styles.panelIcon}>
                    <Icon name={ADDRESS_ICON} size="sm" />
                  </span>
                  {t("commons.adress.label", "Adresse")}
                </p>
                <div className={styles.addrList}>
                  <div className={styles.addrRow}>
                    <span className={styles.addrIcon}>
                      <Icon name={ADDRESS_ICON} size="sm" />
                    </span>
                    <div className={styles.addrText}>
                      <small>{t("commons.adress.label", "Adresse")}</small>
                      <Text as="p">
                        {line1 && <b>{line1}</b>}
                        {line1 && <br />}
                        {line2 && (
                          <>
                            {line2}
                            <br />
                          </>
                        )}
                        {line3 || t("commons.notSpecified", "Non renseigné")}
                      </Text>
                    </div>
                  </div>
                  <div className={styles.addrRow}>
                    <span className={styles.addrIcon}>
                      <Icon name={COUNTRY_ICON} size="sm" />
                    </span>
                    <div className={styles.addrText}>
                      <small>{t("commons.adress.country", "Pays")}</small>
                      <Text as="p">
                        {organisme?.address?.country || t("commons.notSpecified", "Non renseigné")}
                      </Text>
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.panel}>
                <p className={styles.panelTitle}>
                  <span className={styles.panelIcon}>
                    <Icon name={STATUS_ICON} size="sm" />
                  </span>
                  {t("organism.status.title", "Statut du dossier")}
                </p>
                <div className={styles.statusTimelineRow}>
                  <Timeline steps={statusSteps} />
                </div>
                <div className={styles.noteList}>
                  <div className={styles.noteItem}>
                    <span className={styles.noteIcon}>
                      <Icon name={NOTE_ICON_PUBLIC} size="sm" />
                    </span>
                    <span className={styles.noteText}>
                      <strong>{t("organism.notes.publicTitle", "Informations publiques")}</strong>
                      <span>
                        {t(
                          "organism.notes.publicBody",
                          "Ces données sont visibles par les voyageurs sur vos fiches circuits."
                        )}
                      </span>
                    </span>
                  </div>
                  <div className={styles.noteItem}>
                    <span className={styles.noteIcon}>
                      <Icon name={NOTE_ICON_REVALIDATE} size="sm" />
                    </span>
                    <span className={styles.noteText}>
                      <strong>{t("organism.notes.revalidateTitle", "Mise à jour")}</strong>
                      <span>
                        {t(
                          "organism.notes.revalidateBody",
                          "Toute modification peut nécessiter une nouvelle validation avant publication."
                        )}
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.orgFooter}>
            <span className={styles.footerHint}>
              <Icon name="iconCompliance" size="sm" />
              {t("organism.rgpdHint", "Données chiffrées & conformes RGPD")}
            </span>
            {canEdit && (
              <div className={styles.footerActions}>
                <Button
                  variant="primary"
                  iconName="iconPenToSquare"
                  iconPosition="left"
                  label={
                    organisme
                      ? t("commons.allActions.edit")
                      : t("commons.allActions.add")
                  }
                  onClick={() => navigate(organisme ? `${organisme.id}` : "add")}
                />
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
};

export default Organisme;
